import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Menu, Checkbox, Typography, Tooltip } from "antd";
import styled from "styled-components";

import ColumnSelectorIcon from "../../assets/svg/ColumnSelectorIcon";
import { WHITE } from "../../constant/colors";

const { Text } = Typography;

export const FACILITY_COLUMNS_STORAGE_KEY = "facilities-settings-columns";

// Column keys, in table order. `nameNpi` is mandatory (PRD: Name/NPI can never be hidden).
export const FACILITY_COLUMNS = Object.freeze([
  { key: "nameNpi", labelKey: "facility_management.list.name_npi", mandatory: true },
  { key: "taxId", labelKey: "facility_management.list.tax_id" },
  { key: "address", labelKey: "facility_management.list.address" },
  { key: "suiteNumber", labelKey: "facility_management.list.suite_number" },
  { key: "city", labelKey: "facility_management.list.city" },
  { key: "state", labelKey: "facility_management.list.state" },
  { key: "phone", labelKey: "facility_management.list.phone" },
  { key: "zipCode", labelKey: "facility_management.list.zip_code" },
  { key: "matchingString", labelKey: "facility_management.list.matching_string" }
]);

export const readHiddenColumns = () => {
  try {
    const raw = localStorage.getItem(FACILITY_COLUMNS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key) => key !== "nameNpi") : [];
  } catch (error) {
    return [];
  }
};

export const writeHiddenColumns = (hiddenColumns) => {
  try {
    localStorage.setItem(FACILITY_COLUMNS_STORAGE_KEY, JSON.stringify(hiddenColumns));
  } catch (error) {
    console.error(`could not save facility column selection: ${error}`);
  }
};

const StyledMenu = styled(Menu)`
  overflow: auto;
  max-height: calc(100vh - 300px);
  background: ${WHITE};
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;

/**
 * Show/hide columns for the Facilities table (MVP-31132). Self-contained on purpose: the dashboard
 * ColumnSelector is bound to `tableColumns` / `selectedViews` and dashboard Pendo buckets.
 */
export const FacilityColumnSelector = ({ hiddenColumns, onChange }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const toggle = (key) => {
    const next = hiddenColumns.includes(key) ? hiddenColumns.filter((k) => k !== key) : [...hiddenColumns, key];
    onChange(next);
  };

  const menu = useMemo(
    () => (
      <StyledMenu onClick={({ key }) => toggle(key)}>
        {FACILITY_COLUMNS.map((column) => (
          <Menu.Item disabled={column.mandatory} key={column.key} id={`facility_column_selector_${column.key}`}>
            <Checkbox disabled={column.mandatory} checked={!hiddenColumns.includes(column.key)} />
            <Text disabled={column.mandatory} style={{ marginLeft: 8 }}>
              {t(column.labelKey)}
            </Text>
          </Menu.Item>
        ))}
      </StyledMenu>
    ),
    [hiddenColumns, t]
  );

  return (
    <Dropdown overlay={menu} trigger={["click"]} open={visible} onOpenChange={setVisible} placement="bottomRight">
      <Tooltip title={t("facility_management.list.column_selector_tooltip")}>
        <IconButton
          type="button"
          id="Facilities_ColumnSelector"
          aria-label={t("facility_management.list.column_selector_tooltip")}
          aria-haspopup="menu"
          aria-expanded={visible}
        >
          <ColumnSelectorIcon />
        </IconButton>
      </Tooltip>
    </Dropdown>
  );
};

export default FacilityColumnSelector;
